import { RouteFragmentStatistics, RouteStatisticsStreetSegment, RouteSegment } from '@/api/main/types.gen'
import { percentage } from '@/utils/math'
import { round, sum } from 'lodash-es'
import { useMemo } from 'react'
import { GenericTable, TableColumn } from './Table/GenericTable'
import { useFilter } from './Table/useFilter'
import { useGenericTable } from './Table/useGenericTable'
import { TableActions } from './Table/types'
import { useTranslation } from 'react-i18next'

interface EnhancedFragmentData {
  fragment: string
  billableKm: number
  singlePassCount: number
  doublePassCount: number
  length: number
  startIndex: number
  endIndex: number
}

function FragmentsTable({
  fragments,
  streetSegments,
  routePlan,
  selectedFragment,
}: {
  fragments: RouteFragmentStatistics[]
  streetSegments: { [key: string]: RouteStatisticsStreetSegment }
  routePlan: RouteSegment[]
  selectedFragment: number
}) {
  const { t } = useTranslation('routeManagement')

  const fragmentTableHeaders: TableColumn<EnhancedFragmentData>[] = [
    {
      key: 'fragment',
      label: t('fragment'),
      visible: true,
      sortable: true,
      align: 'left',
      sortFunction: (a, b) => {
        const aIsFullRoute = a.startIndex === 0 && a.endIndex === routePlan.length
        const bIsFullRoute = b.startIndex === 0 && b.endIndex === routePlan.length

        if (aIsFullRoute && !bIsFullRoute) return 1
        if (!aIsFullRoute && bIsFullRoute) return -1
        if (aIsFullRoute && bIsFullRoute) return 0

        return a.startIndex - b.startIndex
      },
      render: (item: EnhancedFragmentData) => (
        <span
          style={{
            backgroundColor:
              item.startIndex === fragments[selectedFragment]?.start_index &&
              item.endIndex === fragments[selectedFragment]?.end_index
                ? 'var(--mantine-color-yellow-2)'
                : undefined,
          }}
        >
          {item.fragment}
        </span>
      ),
    },
    {
      key: 'length',
      label: t('length'),
      visible: true,
      sortable: true,
      align: 'right',
      render: (item: EnhancedFragmentData) => (
        <span
          style={{
            backgroundColor:
              item.startIndex === fragments[selectedFragment]?.start_index &&
              item.endIndex === fragments[selectedFragment]?.end_index
                ? 'var(--mantine-color-yellow-2)'
                : undefined,
          }}
        >
          {round(item.length, 1)}
        </span>
      ),
    },
  ]

  const segmentRequirements = useMemo(() => {
    const segmentRequirements: Array<[string, string, number]> = []

    Object.values(streetSegments).forEach(it => {
      if (it.requested_passes > 0) {
        segmentRequirements.push([it.street_segment_id, it.source_node_id, it.requested_passes])
      }
      if (it.requested_reverse_passes > 0) {
        segmentRequirements.push([it.street_segment_id, it.destination_node_id, it.requested_reverse_passes])
      }
    })

    return segmentRequirements
  }, [streetSegments])

  const enhancedData: EnhancedFragmentData[] = useMemo(() => {
    return fragments.map(({ start_index, end_index, potential_billable_km }) => {
      const satisfiedRequirements = segmentRequirements.filter(
        ([segmentId, sourceNodeId, requestedPasses]) =>
          routePlan
            .slice(start_index, end_index)
            .filter((it: RouteSegment) => it.street_segment_id === segmentId && it.entrance_node_id === sourceNodeId)
            .length >= requestedPasses
      )

      return {
        fragment:
          start_index === 0 && end_index === routePlan.length
            ? '100%'
            : `${percentage(start_index, routePlan.length)}-${percentage(end_index, routePlan.length)}`,
        billableKm: sum(potential_billable_km.map(it => it.value)),
        singlePassCount: satisfiedRequirements.filter(([, , requestedPasses]) => requestedPasses === 1).length,
        doublePassCount: satisfiedRequirements.filter(([, , requestedPasses]) => requestedPasses === 2).length,
        length: sum(
          routePlan.slice(start_index, end_index).map(({ street_segment_id: id }) => streetSegments[id]?.length_km || 0)
        ),
        startIndex: start_index,
        endIndex: end_index,
      }
    })
  }, [fragments, segmentRequirements, routePlan, streetSegments])

  const { filteredData, handleFilterFieldChange, handleSearchChange, search, filterField } = useFilter({
    data: enhancedData,
  })

  const {
    currentPage,
    tableColumns,
    paginatedData,
    handlePageChange,
    setTableColumns,
    sortDirection,
    setSortField,
    setSortDirection,
    sortField,
  } = useGenericTable({
    itemsPerPage: 20,
    defaultSortField: 'fragment',
    defaultSortDirection: 'asc',
    tableKey: 'fragmentsTableColumns',
    columns: fragmentTableHeaders,
    filteredData,
  })

  return (
    <GenericTable
      data={enhancedData}
      isLoading={false}
      error={null}
      currentPage={currentPage}
      onPageChange={handlePageChange}
      onFilterFieldChange={handleFilterFieldChange}
      onSearchChange={handleSearchChange}
      getRowKey={item => `${item.startIndex}-${item.endIndex}`}
      emptyMessage={t('noFragmentsFoundForRoute')}
      paginatedData={paginatedData}
      filteredData={filteredData}
      itemsPerPage={20}
      search={search}
      columns={tableColumns}
      onColumnsChange={setTableColumns}
      filterField={filterField}
      isDebouncing={false}
      verticalSpacing="xs"
      hideCsvDownload={false}
      csvFileName="route-fragments.csv"
      restrict={[TableActions.Filter]}
      sortDirection={sortDirection}
      onSortDirectionChange={setSortDirection}
      sortField={sortField}
      onSortFieldChange={setSortField}
    />
  )
}

export default FragmentsTable
